import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Grid, IconButton } from '@mui/material';
import DosenTooltip from '../../../../../../../components/ui/DosenTooltip';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import RuleIcon from '@mui/icons-material/Rule';
import { useAppDispatch, useAppSelector } from '../../../../../../../redux/hooks';
import { grpDetailsActions } from '../../../../../../../redux/slices/group-details/groupDetails';
import ConfirmDialog from '../../../../../../../components/DialogUi/ConfirmDialog';
import { InputField, CtaButton } from './components/SettingsUI';
import { ConfigurationView } from './components/ConfigurationView';
import { DesignView } from './components/DesignView';
import { ReviewModal } from './components/ReviewModal';
import { useSettingsLayoutState } from './hooks/useSettingsLayoutState';
import { useSettingsLayoutSave } from './hooks/useSettingsLayoutSave';
import DueDateModal, { ProgramDueDateData } from '../settings components/Dialog/DueDateModal';
import { API } from '../../../../../../../api';
import { toast } from 'react-toastify';
import { CourseVersioningProvider, useCourseVersioning } from './versioning/CourseVersioningContext';
import { VersioningHeaderControls } from './versioning/VersioningHeaderControls';
import { openRequirements, useRequirementsPilot } from './requirements/useRequirements';
import { VersionHistoryDrawer } from './versioning/VersionHistoryDrawer';
import { PublishUpdateModal } from './versioning/PublishUpdateModal';
import useEditingFrozen from '../../../../../../../hooks/useEditingFrozen';
import useCourseVersioningFlag from '../../../../../../../hooks/useCourseVersioning';
import { useProgramContext } from '../../../../../../../hooks/useProgramContext';
import { EDITING_FROZEN_MESSAGE } from '../../../../../../../utils/constants';
import { EditingFrozenBanner, ReadOnlyWhenFrozen } from '../../../../../../../components/program-freeze/EditingFrozenGuard';

// BE may return the legacy flat shape ({type, value}, pre existing/new split)
// or the current nested shape ({existingMembers, newMembers, firstConfiguredAt}).
// Normalize to the nested shape for the modal — legacy docs show the same
// rule in both tabs until the admin next saves (which upgrades the doc).
function normalizeProgramDueDate(raw: any): Partial<ProgramDueDateData> | undefined {
  if (!raw) return undefined;
  if (raw.existingMembers || raw.newMembers) {
    return { existingMembers: raw.existingMembers ?? undefined, newMembers: raw.newMembers ?? undefined };
  }
  if (raw.type) {
    return {
      existingMembers: { type: raw.type, value: raw.value },
      newMembers: { type: raw.type, value: raw.value }
    };
  }
  return undefined;
}

interface SettingsLayoutProps {
  view?: 'configuration' | 'design';
  refetchKey?: number;
  hideOnboardingFields?: boolean;
  expanded?: boolean;
  onToggleExpanded?: () => void;
  /**
   * Whether leaving now would lose edits: unsaved changes, or (course versioning) a draft
   * autosave not yet written. The Program Editor modal asks before closing on it.
   */
  onPendingChange?: (pending: boolean) => void;
}

// Draft → Publish update model (course versioning, org flag `courseVersioning`).
export function SettingsLayout(props: SettingsLayoutProps) {
  // A frozen org (relaunch clean-up, B4-2) stays read-only — the draft/publish
  // header must not hand it an editing surface.
  const editingFrozen = useEditingFrozen();
  const courseVersioning = useCourseVersioningFlag();
  const { groupId } = useProgramContext();
  const versioningOn = props.view === 'design' && courseVersioning && !editingFrozen && Boolean(groupId);
  return (
    <CourseVersioningProvider enabled={versioningOn} grpId={groupId}>
      <SettingsLayoutInner {...props} versioningOn={versioningOn} />
    </CourseVersioningProvider>
  );
}

function SettingsLayoutInner({
  versioningOn,
  view = 'configuration',
  refetchKey = 0,
  hideOnboardingFields = false,
  expanded = false,
  onToggleExpanded,
  onPendingChange
}: SettingsLayoutProps & { versioningOn: boolean }) {
  const dispatch = useAppDispatch();
  // Program lock — a locked program still shows to learners but Continue /
  // Improve Performance are disabled (enforced FE + BE). Read from groupDetails
  // (the BE returns the flag on the group), toggled from the header padlock.
  const isLocked = useAppSelector((state: any) => state.groupDetails?.data?.locked === true);
  // Relaunch editing freeze (B4-2): the whole program editor is read-only.
  const editingFrozen = useEditingFrozen();
  // Audience Requirements (prototype): the grid's entry point, left of the lock.
  const requirementsOn = useRequirementsPilot();
  // Course versioning: another admin holding the edit lock makes the Designer
  // read-only the same way (banner + swallowed input), with their name.
  const versioning = useCourseVersioning();
  const lockHolder = versioning?.lockHolder ?? null;
  const lockedByOther = Boolean(lockHolder);
  const lockMessage = lockedByOther
    ? `${lockHolder?.name || 'Another admin'} is editing this program right now. You can look around; editing unlocks once they leave.`
    : '';
  const [lockConfirmOpen, setLockConfirmOpen] = useState(false);
  const [lockBusy, setLockBusy] = useState(false);

  const manualSaveHandlerRef = useRef<(() => Promise<boolean>) | null>(null);
  const registerManualSaveHandler = useCallback((handler: (() => Promise<boolean>) | null) => {
    manualSaveHandlerRef.current = handler;
  }, []);

  const onRefetchSuccess = useCallback(
    (freshGroupData: any) => {
      dispatch(grpDetailsActions.updateGroupData(freshGroupData));
    },
    [dispatch]
  );

  const [isSubjectModalOpen, setIsSubjectModalOpen] = useState(false);
  const [newSubjectName, setNewSubjectName] = useState('');
  const [newSubjectType, setNewSubjectType] = useState<'topic' | 'industry'>('topic');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [programDueDateOpen, setProgramDueDateOpen] = useState(false);
  const [programDueDate, setProgramDueDate] = useState<Partial<ProgramDueDateData> | null>(null);

  const state = useSettingsLayoutState(view, refetchKey);
  const {
    grpId,
    orgId,
    location,
    programName,
    setProgramName,
    model,
    setModel,
    participantLabel,
    setParticipantLabel,
    advisorLabel,
    setAdvisorLabel,
    maxMatchesPerMentee,
    setMaxMatchesPerMentee,
    maxMatchesPerMentor,
    setMaxMatchesPerMentor,
    sessionsPerMatch,
    setSessionsPerMatch,
    useAIMatching,
    setUseAIMatching,
    aiMatchingFields,
    setAiMatchingField,
    learningObjectives,
    setLearningObjectives,
    fieldSettings,
    subjects,
    setSubjects,
    designTemplate,
    designLoading,
    designError,
    templateId,
    templateGoalName,
    groupData,
    basicSettingsLoading,
    learningObjectivesLoading,
    groupError,
    isAnyLoading,
    hasPendingChanges,
    currentSnapshot,
    setLastSavedSnapshot,
    isDesignDirty,
    setIsDesignDirty,
    markUserEdited,
    updateField,
    refetchGroupDetails
  } = state;

  const handleToggleLock = useCallback(async () => {
    if (!grpId) return;
    const next = !isLocked;
    setLockBusy(true);
    try {
      await API.updateProgramLock({ grpId, locked: next });
      // updateGroupData REPLACES the whole group doc — passing a single field
      // wiped name/model/goals/grpId out of redux, which reset the designer
      // and made the lock look like it never happened. updateGroupInfo merges.
      dispatch(grpDetailsActions.updateGroupInfo({ locked: next }));
      toast.success(next ? 'Program locked' : 'Program unlocked');
      setLockConfirmOpen(false);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || `Couldn't ${next ? 'lock' : 'unlock'} the program`);
    } finally {
      setLockBusy(false);
    }
  }, [grpId, isLocked, dispatch]);

  const { executeSave, saveProgramName } = useSettingsLayoutSave({
    grpId: grpId || '',
    orgId: orgId || '',
    location: location || '',
    view,
    hasPendingChanges,
    isDesignDirty,
    programName,
    model,
    participantLabel,
    advisorLabel,
    maxMatchesPerMentee,
    maxMatchesPerMentor,
    sessionsPerMatch,
    useAIMatching,
    aiMatchingFields,
    fieldSettings,
    subjects,
    learningObjectives,
    currentSnapshot,
    groupData,
    refetchGroupDetails,
    setLastSavedSnapshot,
    setIsDesignDirty,
    getManualSaveHandler: () => manualSaveHandlerRef.current,
    setIsSaving,
    onRefetchSuccess
  });

  // Versioning on: Publish Update replaces Save Changes, so leaving Program Name saves it, the way
  // the lock and the due date save (docs/BUG-designer-program-rename-not-saved.md).
  const handleProgramNameBlur = useCallback(async () => {
    if (!versioningOn) return;
    try {
      if (!(await saveProgramName())) return;
      dispatch(grpDetailsActions.updateGroupInfo({ name: programName.trim() }));
      toast.success('Program name saved');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "The program name couldn't be saved. Please try again.");
    }
  }, [versioningOn, saveProgramName, dispatch, programName]);

  useEffect(() => {
    setProgramDueDate(normalizeProgramDueDate(groupData?.data?.programDueDate) ?? null);
  }, [groupData?.data?.programDueDate]);

  // With versioning, edits autosave to the draft, so only a write still on its way (or failed) would be lost.
  const draftSaveState = versioning?.saveState;
  const pendingEdits = versioningOn
    ? draftSaveState === 'pending' || draftSaveState === 'saving' || draftSaveState === 'error'
    : hasPendingChanges;
  useEffect(() => {
    onPendingChange?.(pendingEdits);
  }, [pendingEdits, onPendingChange]);

  const handleSaveClick = useCallback(() => {
    if (!hasPendingChanges) return;
    executeSave();
  }, [hasPendingChanges, executeSave]);

  const handleDesignSaveClick = useCallback(() => {
    if (!hasPendingChanges) return;
    setShowReviewModal(true);
  }, [hasPendingChanges]);

  const handleCreateAndSave = useCallback(async () => {
    // Close the confirm modal and save in place. The header button shows its
    // inline "Saving..." state and a toast confirms on success - the same
    // lightweight flow as a configuration/unbuilt save (no full-screen takeover).
    setShowReviewModal(false);
    await executeSave();
  }, [executeSave]);

  const shouldDisableBasicFields = model === 'mentorship' || model === 'masterclass&mentorship';
  const isMasterclassModel = model === 'masterclass';
  const isMentorshipModel = model === 'mentorship';
  const isProgramPublished = Boolean(groupData?.data?.settingsUpdate);

  const handleAddSubject = useCallback(
    (subject: { name: string; type: 'topic' | 'industry' }) => {
      markUserEdited();
      setSubjects((prev) => [...prev, subject]);
    },
    [markUserEdited, setSubjects]
  );

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        bgcolor: '#000',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#1c1c1c'
      }}
    >
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', minHeight: 0 }}>
        {/* Header: Program name + Save */}
        <Box
          sx={{
            width: '100%',
            py: '15px',
            px: 2,
            borderBottom: '1px solid #2A2A2A',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#1c1c1c'
          }}
        >
          <Box sx={{ width: 275, maxWidth: 250, display: 'flex', flexDirection: 'column' }}>
            <InputField
              label="Program Name"
              placeholder="Program Name"
              value={programName}
              onChange={(value) => {
                markUserEdited();
                setProgramName(value);
              }}
              onBlur={handleProgramNameBlur}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 1.5,
              flexShrink: 0
            }}
          >
            {view === 'design' && requirementsOn && (
              <DosenTooltip title="Audience Requirements">
                <IconButton
                  size="small"
                  aria-label="Audience Requirements"
                  data-testid="requirements-course"
                  onClick={() => openRequirements({ kind: 'course' })}
                  sx={{ color: '#F79420' }}
                >
                  <RuleIcon sx={{ fontSize: 26 }} />
                </IconButton>
              </DosenTooltip>
            )}
            {view === 'design' && (
              <DosenTooltip title={isLocked ? 'Unlock program' : 'Lock program'}>
                <IconButton
                  onClick={() => setLockConfirmOpen(true)}
                  size="small"
                  sx={{ color: isLocked ? '#F79420' : '#FDFDFD' }}
                >
                  {isLocked ? <LockIcon sx={{ fontSize: 26 }} /> : <LockOpenIcon sx={{ fontSize: 26 }} />}
                </IconButton>
              </DosenTooltip>
            )}
            {view === 'design' && (
              <DosenTooltip
                title={
                  programDueDate &&
                  ((programDueDate.existingMembers && programDueDate.existingMembers.type !== 'activeNow') ||
                    (programDueDate.newMembers && programDueDate.newMembers.type !== 'activeNow'))
                    ? 'Edit Program Due Date'
                    : 'Add Program Due Date'
                }
              >
                <IconButton onClick={() => setProgramDueDateOpen(true)} size="small" sx={{ color: '#FDFDFD' }}>
                  <CalendarMonthIcon sx={{ fontSize: 28 }} />
                </IconButton>
              </DosenTooltip>
            )}
            {onToggleExpanded && !versioningOn && (
              <DosenTooltip title={expanded ? 'Exit full screen' : 'Full screen'}>
                <IconButton onClick={onToggleExpanded} size="small" sx={{ color: '#FDFDFD' }}>
                  {expanded ? <FullscreenExitIcon sx={{ fontSize: 26 }} /> : <FullscreenIcon sx={{ fontSize: 26 }} />}
                </IconButton>
              </DosenTooltip>
            )}
            {versioningOn ? (
              <VersioningHeaderControls expanded={expanded} onToggleExpanded={onToggleExpanded} />
            ) : (
              <DosenTooltip title={editingFrozen ? EDITING_FROZEN_MESSAGE : ''}>
                <span>
                  {view === 'design' ? (
                    <CtaButton
                      onClick={handleDesignSaveClick}
                      disabled={editingFrozen || !hasPendingChanges || isSaving || isAnyLoading}
                    >
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </CtaButton>
                  ) : (
                    <CtaButton
                      onClick={handleSaveClick}
                      disabled={editingFrozen || !hasPendingChanges || isSaving || isAnyLoading}
                    >
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </CtaButton>
                  )}
                </span>
              </DosenTooltip>
            )}
          </Box>
        </Box>

        <EditingFrozenBanner frozen={editingFrozen} message={EDITING_FROZEN_MESSAGE} sx={{ mx: 2, mt: 2 }} />
        <EditingFrozenBanner frozen={!editingFrozen && lockedByOther} message={lockMessage} sx={{ mx: 2, mt: 2 }} />

        {/* Body: Design or Configuration */}
        {view === 'design' ? (
          <Grid container spacing={0} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <Grid
              item
              xs={12}
              sx={{
                background: '#1C1C1C',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                height: '100%'
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  height: '100%',
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  overflow: 'hidden'
                }}
              >
                <ReadOnlyWhenFrozen frozen={editingFrozen || lockedByOther}>
                <DesignView
                  templateId={templateId}
                  templateGoalName={templateGoalName}
                  grpId={grpId || ''}
                  designTemplate={designTemplate}
                  designLoading={designLoading}
                  designError={designError}
                  onProgramNameChange={(value) => {
                    markUserEdited();
                    setProgramName(value);
                  }}
                  registerSaveHandler={registerManualSaveHandler}
                  onDirtyChange={setIsDesignDirty}
                />
                </ReadOnlyWhenFrozen>
              </Box>
            </Grid>
          </Grid>
        ) : (
          <ConfigurationView
            hideOnboardingFields={hideOnboardingFields}
            programName={programName}
            setProgramName={setProgramName}
            model={model}
            setModel={setModel}
            participantLabel={participantLabel}
            setParticipantLabel={setParticipantLabel}
            advisorLabel={advisorLabel}
            setAdvisorLabel={setAdvisorLabel}
            maxMatchesPerMentee={maxMatchesPerMentee}
            setMaxMatchesPerMentee={setMaxMatchesPerMentee}
            maxMatchesPerMentor={maxMatchesPerMentor}
            setMaxMatchesPerMentor={setMaxMatchesPerMentor}
            sessionsPerMatch={sessionsPerMatch}
            setSessionsPerMatch={setSessionsPerMatch}
            useAIMatching={useAIMatching}
            setUseAIMatching={setUseAIMatching}
            aiMatchingFields={aiMatchingFields}
            setAiMatchingField={setAiMatchingField}
            fieldSettings={fieldSettings}
            updateField={updateField}
            subjects={subjects}
            setSubjects={setSubjects}
            learningObjectives={learningObjectives}
            setLearningObjectives={setLearningObjectives}
            basicSettingsLoading={basicSettingsLoading}
            learningObjectivesLoading={learningObjectivesLoading}
            groupError={!!groupError}
            isProgramPublished={isProgramPublished}
            shouldDisableBasicFields={shouldDisableBasicFields}
            isMentorshipModel={isMentorshipModel}
            isMasterclassModel={isMasterclassModel}
            markUserEdited={markUserEdited}
            onOpenSubjectModal={() => setIsSubjectModalOpen(true)}
            isSubjectModalOpen={isSubjectModalOpen}
            newSubjectName={newSubjectName}
            setNewSubjectName={setNewSubjectName}
            newSubjectType={newSubjectType}
            setNewSubjectType={setNewSubjectType}
            onAddSubject={handleAddSubject}
            onCloseSubjectModal={() => setIsSubjectModalOpen(false)}
          />
        )}
      </Box>

      <ReviewModal open={showReviewModal} onClose={() => setShowReviewModal(false)} onConfirm={handleCreateAndSave} />

      <ConfirmDialog
        open={lockConfirmOpen}
        title={isLocked ? 'Unlock this program?' : 'Lock this program?'}
        message={
          isLocked
            ? 'Learners will be able to continue and improve their lessons again.'
            : 'The program stays visible to learners, but they won’t be able to continue lessons or improve their performance until you unlock it.'
        }
        confirmLabel={isLocked ? 'Unlock' : 'Lock'}
        busy={lockBusy}
        busyLabel={isLocked ? 'Unlocking...' : 'Locking...'}
        onConfirm={handleToggleLock}
        onClose={() => (lockBusy ? null : setLockConfirmOpen(false))}
      />

      <DueDateModal
        open={programDueDateOpen}
        onClose={() => setProgramDueDateOpen(false)}
        mode="program"
        initialProgramData={programDueDate || undefined}
        onSave={() => {}}
        onSaveProgram={async (data) => {
          setProgramDueDate(data);
          setProgramDueDateOpen(false);
          if (grpId && orgId) {
            try {
              const formData = new FormData();
              // Send programDueDate as a JSON-stringified object so the BE
              // Joi validator sees { existingMembers, newMembers } — busboy
              // doesn't decode bracket notation.
              formData.append(
                'programDueDate',
                JSON.stringify({
                  existingMembers: {
                    type: data.existingMembers.type,
                    value: Number(data.existingMembers.value) || 0
                  },
                  newMembers: {
                    type: data.newMembers.type,
                    value: Number(data.newMembers.value) || 0
                  }
                })
              );
              await API.updateGroupInfo({ orgId, grpId, formData });
              refetchGroupDetails();
              toast.success('Program due date saved');
            } catch (e) {
              // A silent failure here already reached a customer once (the
              // modal closed as if saved while the BE rejected the PATCH) —
              // keep the user-visible error.
              // eslint-disable-next-line no-console
              console.error('[Program Due Date] save failed', e);
              toast.error("The due date couldn't be saved. Please try again.");
            }
          }
        }}
      />

      {versioningOn && (
        <>
          <VersionHistoryDrawer />
          <PublishUpdateModal programName={programName} />
        </>
      )}
    </Box>
  );
}
